// src/pages/SettingsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Lock,
  Phone,
  Save,
  Send,
  Clock,
  Plus,
  Pencil,
  CheckCircle,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import config from "../config";
import "./Setting.css";

export default function SettingsPage({ userId: userIdProp, userKey: userKeyProp }) {
  const navigate = useNavigate();

  // --- Get userId (ONLY for password change) ---
  const userId = useMemo(() => {
    if (userIdProp) return String(userIdProp);
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      const data = JSON.parse(raw);
      const id =
        data?.userId ??
        data?.data?.userId ??
        data?.user?.userId ??
        data?.profile?.userId ??
        data?.id ??
        "";
      return id ? String(id) : "";
    } catch {
      return "";
    }
  }, [userIdProp]);

  // --- Get userKey (for WA features) ---
  const userKey = useMemo(() => {
    if (userKeyProp) return String(userKeyProp);
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      const data = JSON.parse(raw);
      const key =
        data?.userKey ??
        data?.data?.userKey ??
        data?.user?.userKey ??
        data?.profile?.userKey ??
        data?.key ??
        data?.user_key ??
        "";
      return key ? String(key) : "";
    } catch {
      return "";
    }
  }, [userKeyProp]);

  // ====================================================
  // 1) WHATSAPP NUMBERS FLOW
  // ====================================================
  // const [whatsappEnabled] = useState(false); // kept for future use

  // Add / edit box
  const [showSecondBox, setShowSecondBox] = useState(false);
  const [secondPhone, setSecondPhone] = useState("");      // 10 digits only
  const [secondLocked, setSecondLocked] = useState(false); // true when editing existing
  const [editingExisting, setEditingExisting] = useState(false); // true when opened from dropdown Add

  // OTP
  const [otp, setOtp] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Devices (multi-select)
  const [devices, setDevices] = useState([]);              // [{id, name}]
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deviceLoadedFromSession, setDeviceLoadedFromSession] = useState(false);

  // UI
  const [waLoading, setWaLoading] = useState(false);
  const [msgWhatsApp, setMsgWhatsApp] = useState("");
  const [errorWhatsApp, setErrorWhatsApp] = useState("");
  const [showJoinLink, setShowJoinLink] = useState(false);

  // Numbers (keep raw + display + enabled flag + devices)
  // [{ value: "8123456789", display: "+91 81234 56789", enabled: true/false, devices: ["390","401"] }]
  const [registeredNumbers, setRegisteredNumbers] = useState([]);

  // Which number row is expanded (for dropdown)
  const [expandedNumber, setExpandedNumber] = useState(null);

  // ======= GLOBAL CONFIRM POPUP STATE (for remove & schedules) =======
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    variant: "confirm", // "confirm" | "info"
  });

  const openConfirmPopup = (title, message, onConfirm, variant = "confirm") => {
    setConfirmPopup({
      open: true,
      title,
      message,
      onConfirm,
      variant,
    });
  };

  const closeConfirmPopup = () => {
    setConfirmPopup((prev) => ({
      ...prev,
      open: false,
      onConfirm: null,
      variant: "confirm",
    }));
  };

  const handleConfirmPopup = () => {
    if (typeof confirmPopup.onConfirm === "function") {
      confirmPopup.onConfirm();
    }
    closeConfirmPopup();
  };

  // Helpers
  const onlyDigits = (v) => (v ? String(v).replace(/\D/g, "") : "");
  const sanitizePhone = (v) => onlyDigits(v).slice(0, 10); // keep 10 digits
  const normalizeTo91 = (val) => {
    const d = onlyDigits(val);
    if (!d) return "";
    if (d.length === 10) return `91${d}`;
    if (d.length === 12 && d.startsWith("91")) return d;
    const tail10 = d.slice(-10);
    return tail10 ? `91${tail10}` : "";
  };
  const formatIntl = (raw91) =>
    raw91 ? `+${raw91.slice(0, 2)} ${raw91.slice(2, 7)} ${raw91.slice(7)}` : "";
  const pickMsg = (e, fallback) =>
    (e?.response?.data?.message && String(e.response.data.message)) ||
    (typeof e?.response?.data === "string" && e.response.data) ||
    (e?.message && String(e.message)) ||
    fallback;
  const extractOtp = (resData) =>
    (String(
      resData?.otp ??
        resData?.code ??
        resData?.data?.otp ??
        resData?.payload?.otp ??
        ""
    )
      .replace(/\D/g, "")
      .slice(0, 6)) || "";

  // Convert "HH:mm:ss" or "HH:mm" to "HH:mm"
  const extractHHmm = (val) => {
    if (!val) return "";
    const s = String(val);
    const [h, m] = s.split(":");
    if (!h || !m) return "";
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  // Persist list (optional)
  const persistRegistered = (arr) => {
    try {
      sessionStorage.setItem("registeredNumbers", JSON.stringify(arr));
    } catch {}
  };
  const loadRegistered = () => {
    try {
      const raw = sessionStorage.getItem("registeredNumbers");
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr
        .filter(
          (x) =>
            x &&
            typeof x.value === "string" &&
            typeof x.display === "string"
        )
        .map((x) => ({
          value: x.value,
          display: x.display,
          enabled: x.enabled === false ? false : true,
          devices: Array.isArray(x.devices)
            ? x.devices.map((d) => String(d))
            : [],
        }));
    } catch {
      return [];
    }
  };

  // ---- Fetch numbers & scheduler from backend ----
  const fetchNumbers = async () => {
    if (!userKey) return;
    try {
      const base = config.BASE_URL || config.baseURL || "";
      const url = `${base}/api/whatsapp/get-user-whatsapp/${encodeURIComponent(
        userKey
      )}`;
      const res = await axios.get(url);

      // Numbers from backend
      const list = Array.isArray(res?.data?.numbers) ? res.data.numbers : [];
      const normMap = new Map();

      list.forEach((it) => {
        const raw = onlyDigits(it?.whatsappNumber);
        if (!raw) return;
        const display = formatIntl(normalizeTo91(raw));

        // detect enabled flag from backend object
        const enabled =
          it?.whatsappEnabled ??
          it?.enabled ??
          it?.isEnabled ??
          it?.receiveData ??
          it?.receive_data ??
          true;

        // devices array from backend (support multiple possible keys)
        const devRaw =
          it?.devices ??
          it?.deviceIds ??
          it?.device_ids ??
          it?.deviceList ??
          it?.device_list ??
          [];
        let deviceIds = [];
        if (Array.isArray(devRaw)) {
          deviceIds = devRaw.map((d) => String(d));
        } else if (typeof devRaw === "string") {
          deviceIds = devRaw
            .split(/[,\s]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        }

        normMap.set(raw, {
          value: raw,
          display,
          enabled: Boolean(enabled),
          devices: deviceIds,
        });
      });

      const norm = Array.from(normMap.values());
      setRegisteredNumbers(norm);
      persistRegistered(norm);

      // Scheduler times from backend
      const sched = res?.data?.scheduler || {};
      const t1 = extractHHmm(sched.time1);
      const t2 = extractHHmm(sched.time2);

      // Schedule A
      if (t1) {
        setScheduleTime(t1);
        setScheduleEnabled(true);
      } else {
        setScheduleTime("");
        setScheduleEnabled(false);
      }

      // Schedule B
      if (t2) {
        setScheduleTime2(t2);
        setScheduleEnabled2(true);
      } else {
        setScheduleTime2("");
        setScheduleEnabled2(false);
      }
    } catch {
      const persisted = loadRegistered();
      setRegisteredNumbers(persisted);
    }
  };

  // Load devices + persisted numbers, then fetch live from backend
  useEffect(() => {
    try {
      const ud = JSON.parse(sessionStorage.getItem("userData") || "{}");

      // 1️⃣ Try generic `devices` first
      let list = Array.isArray(ud?.devices) ? ud.devices : null;

      // 2️⃣ If not present, fall back to login shape:
      //     { flowdevices: [...], packinngdevices: [...] }
      if (!list || list.length === 0) {
        const flow =
          ud?.flowdevices ||
          ud?.flowDevices ||
          ud?.data?.flowdevices ||
          [];
        const pack =
          ud?.packinngdevices ||
          ud?.packingdevices ||
          ud?.data?.packinngdevices ||
          [];
        list = [...flow, ...pack];
      }

      // 3️⃣ Normalize to { id, name } using the same fields
      const norm = Array.isArray(list)
        ? list.map((d, idx) => {
            const deviceId =
              d?.deviceId ??
              d?.deviceIds ??
              d?.id ??
              d?.deviceid ??
              d?.device_id ??
              idx + 1;

            const deviceName =
              d?.deviceName ??
              d?.name ??
              d?.devicename ??
              `Device-${deviceId}`;

            return {
              id: String(deviceId),
              name: String(deviceName),
            };
          })
        : [];

      setDevices(norm);
      setSelectedDeviceIds([]);
      setDeviceLoadedFromSession(true);

      const stored = loadRegistered();
      if (stored.length) setRegisteredNumbers(stored);
    } catch {
      setDevices([]);
      setSelectedDeviceIds([]);
      setDeviceLoadedFromSession(true);
    }

    fetchNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);
  
  const resetSecondBoxFlow = () => {
    setOtp("");
    setServerOtp("");
    setOtpSent(false);
    setOtpVerified(false);
    setErrorWhatsApp("");
  };

  // --- Get OTP (for secondPhone) ---
  const handleGetOtp = async () => {
    setMsgWhatsApp("");
    resetSecondBoxFlow();

    const clean = sanitizePhone(secondPhone);
    if (clean.length !== 10) {
      setErrorWhatsApp("Enter a valid 10-digit WhatsApp number.");
      return;
    }
    const full = `91${clean}`;

    try {
      setWaLoading(true);
      const params = new URLSearchParams();
      params.append("whatsappNumber", full);

      const res = await axios.post(
        `${(config.BASE_URL || config.baseURL)}/api/whatsapp/send-otp`,
        params,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const otpFromServer = extractOtp(res?.data);
      if (!otpFromServer) {
        setErrorWhatsApp(
          "Could not read OTP from server. Please request again."
        );
        setOtpSent(false);
        return;
      }

      setServerOtp(otpFromServer);
      setOtpSent(true);
      setMsgWhatsApp(`OTP sent to +${full}. Enter it below to verify.`);
    } catch (e) {
      setErrorWhatsApp(pickMsg(e, "Error while sending OTP."));
    } finally {
      setWaLoading(false);
    }
  };

  // --- Verify OTP ---
  const handleVerifyOtp = () => {
    setMsgWhatsApp("");
    if (!otp) {
      setErrorWhatsApp("Enter the OTP to verify.");
      return;
    }
    if (!serverOtp) {
      setErrorWhatsApp("Verification not available. Please request OTP again.");
      return;
    }
    const entered = String(otp).replace(/\D/g, "").slice(0, 6);
    if (entered === serverOtp) {
      setOtpVerified(true);
      setErrorWhatsApp("");
      setMsgWhatsApp("OTP verified. Select device(s) and register.");
    } else {
      setOtpVerified(false);
      setErrorWhatsApp("Invalid OTP.");
    }
  };

  // Helper: resolve device names from IDs for a given number item
  const resolveDeviceNames = (item) => {
    if (!item || !Array.isArray(item.devices) || item.devices.length === 0) {
      return [];
    }
    return item.devices.map((id) => {
      const found = devices.find((d) => String(d.id) === String(id));
      return found?.name || `Device ${id}`;
    });
  };

  // When user clicks "Add" inside dropdown of a number → go to device selecting section
  const handleAddDevicesForNumber = (item) => {
    const cleanTen = sanitizePhone(item.value);

    setShowSecondBox(true);
    setEditingExisting(true);
    setSecondPhone(cleanTen);
    setSecondLocked(true); // lock number, can click "Change" if needed

    // Skip OTP for existing number; directly show device selection
    setOtp("");
    setServerOtp("");
    setOtpSent(false);
    setOtpVerified(true);

    const devIds = Array.isArray(item.devices)
      ? item.devices.map((id) => String(id))
      : [];
    setSelectedDeviceIds(devIds);

    setShowJoinLink(false);
    setMsgWhatsApp("");
    setErrorWhatsApp("");

    // Scroll to device selection section
    setTimeout(() => {
      const el = document.getElementById("device-select-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // --- Toggle receive-data for a specific number ---
  const handleToggleReceive = async (rawNumber, nextEnabled) => {
    setErrorWhatsApp("");
    setMsgWhatsApp("");

    if (!userKey) {
      setErrorWhatsApp("User details missing. Please login again.");
      setTimeout(() => navigate("/", { replace: true }), 1000);
      return;
    }

    const clean = onlyDigits(rawNumber);
    if (!clean) {
      setErrorWhatsApp("Invalid WhatsApp number.");
      return;
    }

    const payload = {
      userKey: String(userKey),
      whatsappNumber: clean,
      whatsappEnabled: nextEnabled,
    };

    try {
      setWaLoading(true);
      const res = await axios.post(
        `${(config.BASE_URL || config.baseURL)}/api/whatsapp/update-whatsapp`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status >= 200 && res.status < 300) {
        // update local state
        setRegisteredNumbers((prev) => {
          const updated = prev.map((n) =>
            n.value === clean ? { ...n, enabled: nextEnabled } : n
          );
          persistRegistered(updated);
          return updated;
        });

        setMsgWhatsApp(
          res?.data?.message ||
            (nextEnabled
              ? "Data receive enabled for this number."
              : "Data receive disabled for this number.")
        );
      } else {
        setErrorWhatsApp("Failed to update receive setting.");
      }
    } catch (e) {
      const backendMsg = pickMsg(e, "Error while updating receive setting.");
      setErrorWhatsApp(backendMsg);
    } finally {
      setWaLoading(false);
    }
  };

  // --- Helper: actually remove number (after confirm popup) ---
  const doRemoveNumber = async (plainDigits) => {
    setErrorWhatsApp("");
    setMsgWhatsApp("");
    setWaLoading(true);

    const base = config.BASE_URL || config.baseURL || "";
    const url = `${base}/api/whatsapp/delete-number`;
    const fullUrl = `${url}?userKey=${encodeURIComponent(
      userKey
    )}&whatsappNumber=${encodeURIComponent(plainDigits)}`;

    try {
      const res = await axios.delete(fullUrl);
      if (res.status >= 200 && res.status < 300) {
        await fetchNumbers(); // refresh list
        setMsgWhatsApp(res?.data?.message || "Number removed successfully.");
      } else {
        setErrorWhatsApp("Failed to remove number.");
      }
    } catch (e) {
      const backendMsg =
        e?.response?.data?.message ||
        (typeof e?.response?.data === "string" && e.response.data) ||
        e?.message ||
        "Error removing number.";
      setErrorWhatsApp(backendMsg);
    } finally {
      setWaLoading(false);
    }
  };

  // --- Remove a specific number (now using popup) ---
  const handleRemoveNumber = (rawNumber) => {
    if (!userKey) {
      setErrorWhatsApp("User details missing. Please login again.");
      setTimeout(() => navigate("/", { replace: true }), 1000);
      return;
    }

    const plainDigits = onlyDigits(rawNumber);
    if (!plainDigits) {
      setErrorWhatsApp("Invalid WhatsApp number.");
      return;
    }

    const confirmText = formatIntl(normalizeTo91(plainDigits));

    openConfirmPopup(
      "Remove WhatsApp Number",
      `Are you sure you want to remove ${confirmText} ?`,
      () => doRemoveNumber(plainDigits)
    );
  };

  // --- Register number to selected devices (multiple) ---
  const handleRegisterNumber = async () => {
    setErrorWhatsApp("");
    setMsgWhatsApp("");

    // enforce max 3 numbers only for NEW numbers
    if (!editingExisting && registeredNumbers.length >= 3) {
      setErrorWhatsApp(
        "You can register up to 3 WhatsApp numbers only. Remove one to add a new number."
      );
      return;
    }

    if (!userKey) {
      setErrorWhatsApp("User details missing. Please login again.");
      setTimeout(() => navigate("/", { replace: true }), 1000);
      return;
    }

    const clean = sanitizePhone(secondPhone); // "8123456789"
    if (clean.length !== 10) {
      setErrorWhatsApp("Enter a valid 10-digit WhatsApp number.");
      return;
    }

    if (!otpVerified) {
      setErrorWhatsApp("Verify OTP first.");
      return;
    }

    if (devices.length > 0 && selectedDeviceIds.length === 0) {
      setErrorWhatsApp("Select at least one device.");
      return;
    }

    // Convert selected device IDs (string) -> number array [390, 401]
    const devicesNumeric = selectedDeviceIds
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n));

    const payload = {
      userKey: String(userKey),
      whatsappNumber: clean,      // 10-digit only (8123...)
      // Default: ON when registering/updating
      whatsappEnabled: true,
      devices: devicesNumeric,    // [390, 401] → deviceId only
    };

    try {
      setWaLoading(true);
      const res = await axios.post(
        `${(config.BASE_URL || config.baseURL)}/api/whatsapp/update-whatsapp`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status >= 200 && res.status < 300) {
        await fetchNumbers(); // Refresh list (numbers + scheduler)
        setShowSecondBox(false);
        setSecondPhone("");
        setSecondLocked(false);
        setSelectedDeviceIds([]);
        resetSecondBoxFlow();
        setEditingExisting(false);
        setMsgWhatsApp(
          res?.data?.message || "Number registered successfully."
        );
      } else {
        setErrorWhatsApp("Failed to register number.");
      }
    } catch (e) {
      const backendMsg = pickMsg(e, "");
      const looksInvalid =
        e?.response?.status === 400 ||
        e?.response?.status === 401 ||
        /invalid|incorrect|wrong/i.test(backendMsg || "");
      setErrorWhatsApp(
        looksInvalid ? "Invalid OTP." : backendMsg || "Error registering number."
      );
    } finally {
      setWaLoading(false);
    }
  };

  // ====================================================
  // 2) PASSWORD UPDATE
  // ====================================================
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msgPassword, setMsgPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  const UPDATE_CREDENTIALS_ENDPOINT = `${
    config.BASE_URL || config.baseURL
  }/api/auth/update-credentials`;

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setErrorPassword("");
    setMsgPassword("");

    if (!userId) {
      setErrorPassword("User details missing. Please login again.");
      setTimeout(() => navigate("/", { replace: true }), 1000);
      return;
    }
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorPassword("All fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorPassword("New password and Confirm password do not match.");
      return;
    }

    const payload = {
      oldUserId: String(userId),
      oldPassword: String(currentPassword),
      newPassword: String(newPassword),
    };

    try {
      setPwdLoading(true);
      const res = await axios.post(UPDATE_CREDENTIALS_ENDPOINT, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (
        res.status >= 200 &&
        res.status < 300 &&
        (res.data?.updated ?? true)
      ) {
        setMsgPassword("Password updated successfully.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setErrorPassword("Failed to update password.");
      }
    } catch (e) {
      const backendMsg = pickMsg(e, "Error while updating password.");
      setErrorPassword(backendMsg);
    } finally {
      setPwdLoading(false);
    }
  };

  // ====================================================
  // 3) WHATSAPP SCHEDULE (A & B)
  // ====================================================
  const SCHEDULE_ENDPOINT = `${
    config.BASE_URL || config.baseURL || ""
  }/api/whatsapp/update`;

  // Scheduler A
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [msgSchedule, setMsgSchedule] = useState("");
  const [errorSchedule, setErrorSchedule] = useState("");
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Scheduler B
  const [scheduleTime2, setScheduleTime2] = useState("");
  const [scheduleEnabled2, setScheduleEnabled2] = useState(true);
  const [msgSchedule2, setMsgSchedule2] = useState("");
  const [errorSchedule2, setErrorSchedule2] = useState("");
  const [scheduleLoading2, setScheduleLoading2] = useState(false);

  // ====================================================
  // AUTO HIDE ALL SUCCESS / ERROR MESSAGES AFTER 5 SECONDS
  // ====================================================
  // WhatsApp messages
  useEffect(() => {
    if (!msgWhatsApp && !errorWhatsApp) return;
    const timer = setTimeout(() => {
      setMsgWhatsApp("");
      setErrorWhatsApp("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [msgWhatsApp, errorWhatsApp]);

  // Password messages
  useEffect(() => {
    if (!msgPassword && !errorPassword) return;
    const timer = setTimeout(() => {
      setMsgPassword("");
      setErrorPassword("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [msgPassword, errorPassword]);

  // Schedule A messages
  useEffect(() => {
    if (!msgSchedule && !errorSchedule) return;
    const timer = setTimeout(() => {
      setMsgSchedule("");
      setErrorSchedule("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [msgSchedule, errorSchedule]);

  // Schedule B messages
  useEffect(() => {
    if (!msgSchedule2 && !errorSchedule2) return;
    const timer = setTimeout(() => {
      setMsgSchedule2("");
      setErrorSchedule2("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [msgSchedule2, errorSchedule2]);

  // Helper to post a single schedule (A or B)
  const postOneSchedule = async (tag, time, isEnabled) => {
    if (!userKey) return { ok: false, error: "User details missing." };
    if (!time) return { ok: false, error: `Time not selected for ${tag}` };

    const [hour = "", minute = ""] = String(time).split(":");
    const hh = String(hour).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    const hhmm = `${hh}:${mm}`;

    const payload = isEnabled
      ? { userKey, time: hhmm }         // ON
      : { userKey, removetime: hhmm }; // OFF

    try {
      const res = await axios.post(SCHEDULE_ENDPOINT, payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status >= 200 && res.status < 300) {
        const msg =
          (typeof res?.data?.message === "string" && res.data.message) ||
          (isEnabled ? "Schedule set." : "Schedule removed.");
        return { ok: true, message: msg };
      }
      return { ok: false, error: "Failed to save." };
    } catch (e) {
      const errText =
        (e?.response?.data?.message &&
          String(e.response.data.message)) ||
        (typeof e?.response?.data === "string" && e.response.data) ||
        e?.message ||
        "Error.";
      return { ok: false, error: errText };
    }
  };

  // Helper: update schedule A after confirm popup
  const updateScheduleA = async (next) => {
    setScheduleLoading(true);
    setMsgSchedule("");
    setErrorSchedule("");

    const resultA = await postOneSchedule("A", scheduleTime, next);

    if (resultA.ok) {
      setScheduleEnabled(next);
      setMsgSchedule(resultA.message);
    } else if (resultA?.error) {
      setErrorSchedule(resultA.error);
    } else {
      setErrorSchedule("Failed to update Schedule A.");
    }

    setScheduleLoading(false);
  };

  // Helper: update schedule B after confirm popup
  const updateScheduleB = async (next) => {
    setScheduleLoading2(true);
    setMsgSchedule2("");
    setErrorSchedule2("");

    const resultB = await postOneSchedule("B", scheduleTime2, next);

    if (resultB.ok) {
      setScheduleEnabled2(next);
      setMsgSchedule2(resultB.message);
    } else if (resultB?.error) {
      setErrorSchedule2(resultB.error);
    } else {
      setErrorSchedule2("Failed to update Schedule B.");
    }

    setScheduleLoading2(false);
  };

  // Confirm ON/OFF for Schedule A and send to backend (via popup)
  const handleToggleScheduleA = () => {
    const next = !scheduleEnabled;

    // If time not selected -> show info popup with only OK
    if (!scheduleTime) {
      openConfirmPopup(
        "Select Time",
        "Please select Hour and Minute for Schedule A before turning it ON or OFF.",
        null,
        "info"
      );
      return;
    }

    const text = next ? "turn ON Schedule A?" : "turn OFF Schedule A?";

    openConfirmPopup(
      "Confirm Schedule A",
      `Are you sure you want to ${text}`,
      () => updateScheduleA(next),
      "confirm"
    );
  };

  // Confirm ON/OFF for Schedule B and send to backend (via popup)
  const handleToggleScheduleB = () => {
    const next = !scheduleEnabled2;

    // If time not selected -> show info popup with only OK
    if (!scheduleTime2) {
      openConfirmPopup(
        "Select Time",
        "Please select Hour and Minute for Schedule B before turning it ON or OFF.",
        null,
        "info"
      );
      return;
    }

    const text = next ? "turn ON Schedule B?" : "turn OFF Schedule B?";

    openConfirmPopup(
      "Confirm Schedule B",
      `Are you sure you want to ${text}`,
      () => updateScheduleB(next),
      "confirm"
    );
  };

  // ====================================================
  // UI
  // ====================================================
  const onToggleDevice = (id) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const canShowAdd = registeredNumbers.length < 3;

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center p-6">
      <div className="w-full max-w-4xl space-y-6">
        {/* Numbers Section */}
        <div className="change-wa-section">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5" />
            WhatsApp Numbers
          </h2>

          {/* Numbered list */}
          {registeredNumbers.length > 0 && (
            <ol style={{ listStyle: "decimal", paddingLeft: "1.25rem" }}>
              {registeredNumbers.map((item, idx) => {
                const isExpanded = expandedNumber === item.value;
                const deviceNames = resolveDeviceNames(item);

                return (
                  <li
                    key={`${item.value}-${idx}`}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    <div
                      className="prev-number-box flex items-center gap-3"
                      style={{ marginTop: "0.25rem" }}
                    >
                      {/* LEFT: number + arrow on right side of number */}
                      <div className="flex items-center gap-2">
                        <div className="value">{item.display}</div>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedNumber((prev) =>
                              prev === item.value ? null : item.value
                            )
                          }
                          className="flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                          title={
                            isExpanded
                              ? "Hide linked devices"
                              : "Show linked devices"
                          }
                        >
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>

                      {/* CENTER: toggle */}
                      <div className="flex-1 flex justify-center">
                        <label className="small-toggle">
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={() =>
                              handleToggleReceive(item.value, !item.enabled)
                            }
                            disabled={waLoading}
                          />
                          <span
                            className={`small-toggle-pill ${
                              item.enabled ? "on" : "off"
                            }`}
                          >
                            <span className="small-toggle-dot" />
                            <span className="small-toggle-label">
                              {item.enabled ? "ON" : "OFF"}
                            </span>
                          </span>
                        </label>
                      </div>

                      {/* RIGHT: Remove button */}
                      <div>
                        <button
                          type="button"
                          onClick={() => handleRemoveNumber(item.value)}
                          className="bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-md hover:bg-red-100 flex items-center gap-1"
                          disabled={waLoading}
                          title="Remove this number"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Expanded dropdown - show linked devices + Add button */}
                    {isExpanded && (
                      <div className="mt-2 ml-8 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                        <div className="text-xs font-medium text-gray-600">
                          Linked devices:
                        </div>

                        {deviceNames.length > 0 ? (
                          <ul className="list-disc ml-4 text-sm text-gray-700 space-y-1">
                            {deviceNames.map((name, i) => (
                              <li key={i}>{name}</li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-sm text-gray-500">
                            No devices linked for this number.
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => handleAddDevicesForNumber(item)}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 border border-gray-300 rounded-full hover:bg-gray-100"
                        >
                          <Plus className="w-4 h-4" />
                          Add Devices
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {/* Add (hidden when count === 3) */}
          {canShowAdd && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "0.75rem",
              }}
            >
              <button  
                type="button"
                onClick={() => {
                  setShowSecondBox(true);
                  setSecondPhone("");
                  setSecondLocked(false);
                  resetSecondBoxFlow();
                  setMsgWhatsApp("");
                  setShowJoinLink(true);
                  setEditingExisting(false);
                }}
                className="btn-add"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          )}

          {/* Join link */}
          {showJoinLink && (
            <div style={{ marginTop: "0.5rem" }}>
              <a
                href="https://wa.me/919840139203?text=JOIN%20EMMPPE"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowJoinLink(false)}
                style={{
                  color: "#1a8a34",
                  textDecoration: "underline",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Click here to join EMMPPE on WhatsApp
              </a>
            </div>
          )}

          {/* Add / Edit Box */}
          {showSecondBox && (canShowAdd || editingExisting) && (
            <div className="new-number-box">
              <div
                className="text-sm text-gray-600"
                style={{ marginBottom: "0.5rem" }}
              >
                {editingExisting ? "Selected Number" : "New Number"}
              </div>

              <div className="actions-inline">
                <span className="country-code">+91</span>
                <input
                  type="text"
                  value={secondPhone}
                  onChange={(e) => {
                    setSecondPhone(sanitizePhone(e.target.value));
                    setShowJoinLink(false);
                  }}
                  disabled={secondLocked}
                  maxLength={10}
                  placeholder="Enter 10-digit number"
                  className={`phone-input ${
                    secondLocked ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                  inputMode="numeric"
                  autoComplete="tel"
                />

                {!otpSent && !secondLocked && !editingExisting && (
                  <button
                    className="btn-primary"
                    onClick={handleGetOtp}
                    disabled={
                      waLoading || sanitizePhone(secondPhone).length !== 10
                    }
                    title={
                      sanitizePhone(secondPhone).length !== 10
                        ? "Enter 10 digits"
                        : "Send OTP"
                    }
                  >
                    <Send className="w-4 h-4" />
                    {waLoading ? "Sending…" : "Get OTP"}
                  </button>
                )}

                <div className="mobile-btn-row">
                {secondLocked && (
                  <button
                    type="button"
                    onClick={() => {
                      setSecondLocked(false);
                      resetSecondBoxFlow();
                      setEditingExisting(false);
                      setMsgWhatsApp(
                        "You can edit the number now. Get OTP again." 
                      );
                    }}
                    className="btn-change"
                  >
                    <Pencil className="w-4 h-4" />
                    Change
                  </button>
                )}
                </div>
              </div>

              {/* OTP (only for new numbers) */}
              {otpSent && !otpVerified && !editingExisting && (
                <div className="otp-section">
                  <input
                    type="text"
                    value={otp}
                    maxLength={6}
                    placeholder="Enter OTP"
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, ""))
                    }
                    className="otp-input"
                    inputMode="numeric"
                  />
                  <button
                    className="btn-verify"
                    onClick={handleVerifyOtp}
                    disabled={waLoading || !otp}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Verify OTP
                  </button>
                </div>
              )}

              {/* After OTP verified (new) OR editing existing – device selection + Register */}
              {otpVerified && (
                <div
                  className="space-y-4"
                  style={{ marginTop: "1rem" }}
                  id="device-select-section"
                >
                  <div>
                    <div
                      className="text-sm text-gray-600"
                      style={{ marginBottom: "0.25rem" }}
                    > 
                      Select Devices
                    </div>
                    {!deviceLoadedFromSession ? (
                      <div className="text-sm text-gray-500">
                        Loading devices…
                      </div>
                    ) : devices.length > 0 ? (
                      <div className="grid grid-cols-1 gap-2">
                        {devices.map((d) => (
                          <label
                            key={d.id}
                            className="flex items-center gap-3 border rounded-lg px-3 py-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedDeviceIds.includes(d.id)}
                              onChange={() => onToggleDevice(d.id)}
                            />
                            <span>{d.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="text-sm"
                        style={{ color: "#b91c1c" }}
                      >
                        No device names found in your session. Please ensure
                        userData contains device info.
                      </div>
                    )}
                    {devices.length > 0 &&
                      selectedDeviceIds.length === 0 && (
                        <div
                          className="text-xs mt-1"
                          style={{ color: "#b45309" }}
                        >
                          Tip: Select at least one device to enable the
                          Register button.
                        </div>
                      )}
                  </div>
                  <div className="mobile-btn-row">
                  <button
                    className="btn-primary"
                    onClick={handleRegisterNumber}
                    disabled={
                      waLoading ||
                      (devices.length > 0 &&
                        selectedDeviceIds.length === 0) ||
                      (!editingExisting && registeredNumbers.length >= 3)
                    }
                  >
                    <Save className="w-4 h-4" />
                    {waLoading ? "Registering…" : "Register"}
                  </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {(msgWhatsApp || errorWhatsApp) && (
            <div
              className={`msg-box ${
                errorWhatsApp ? "error" : "success"
              }`}
            >
              {errorWhatsApp || msgWhatsApp}
            </div>
          )}
        </div>

        {/* Two cards side-by-side */}
        <div className="two-col">
          {/* Change Password */}
          <div className="bg-white shadow-md rounded-2xl p-6 h-full">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-700 mb-4">
              <Lock className="w-5 h-5 text-red-500" />
              Change Password
            </h2>

            <form
              onSubmit={handlePasswordUpdate}
              className="space-y-4"
              autoComplete="off"
            >
              <input
                type="password"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full border rounded-lg px-4 py-2"
                required
              />
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border rounded-lg px-4 py-2" 
                required
              />  
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border rounded-lg px-4 py-2"
                required
              />

              <button
                type="submit"
                className="bg-red-600 text-white flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-red-700"
                disabled={pwdLoading}
              >
                <Save className="w-4 h-4" />
                {pwdLoading ? "Saving…" : "Save Password"}
              </button>

              {(msgPassword || errorPassword) && (
                <div
                  className={`mt-3 px-3 py-2 rounded-lg ${
                    errorPassword
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-900"
                  }`}
                >
                  {errorPassword || msgPassword}
                </div>
              )}
            </form>
          </div>

          {/* WhatsApp Schedules (Two blocks) */}
          <div className="bg-white shadow-md rounded-2xl p-6 h-full space-y-8">
            {/* Scheduler A */}
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-700 mb-4">
                <Clock className="w-5 h-5 text-green-500" />
                WhatsApp Schedule A
              </h2>

              {/* Row: Hour | Minute | On/Off */}
              <div className="flex items-center gap-4 flex-wrap">
                <select
                  value={scheduleTime.split(":")[0] || ""}
                  onChange={(e) => {
                    const hour = e.target.value.padStart(2, "0");
                    const minute = scheduleTime.split(":")[1] || "00";
                    const next = `${hour}:${minute}`;
                    setScheduleTime(next);
                  }}
                  className="border rounded-lg px-4 py-2"
                  disabled={scheduleLoading}
                >
                  <option value="">Hour</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option
                      key={i}
                      value={String(i).padStart(2, "0")}
                    >
                      {String(i).padStart(2, "0")}
                    </option>
                  ))}
                </select>

                <select
                  value={scheduleTime.split(":")[1] || ""}
                  onChange={(e) => {
                    const minute = e.target.value.padStart(2, "0");
                    const hour = scheduleTime.split(":")[0] || "00";
                    const next = `${hour}:${minute}`;
                    setScheduleTime(next);
                  }}
                  className="border rounded-lg px-4 py-2"
                  disabled={scheduleLoading}
                >
                  <option value="">Minute</option>
                  <option value="00">00</option>
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                </select>

                <div className="flex items-center gap-2">
                  {/* SMALL TOGGLE for Schedule A */}
                  <label className="small-toggle">
                    <input
                      type="checkbox"
                      checked={scheduleEnabled}
                      onChange={handleToggleScheduleA}
                      disabled={scheduleLoading}
                    />
                    <span
                      className={`small-toggle-pill ${
                        scheduleEnabled ? "on" : "off"
                      }`}
                    >
                      <span className="small-toggle-dot" />
                      <span className="small-toggle-label">
                        {scheduleEnabled ? "ON" : "OFF"}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {(msgSchedule || errorSchedule) && (
                <div
                  className={`mt-3 px-3 py-2 rounded-lg ${
                    errorSchedule
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-900"
                  }`}
                >
                  {errorSchedule || msgSchedule}
                </div>
              )}
            </div>

            {/* Scheduler B */}
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-700 mb-4">
                <Clock className="w-5 h-5 text-green-500" />
                WhatsApp Schedule B
              </h2>

              {/* Row: Hour | Minute | On/Off */}
              <div className="flex items-center gap-4 flex-wrap">
                <select
                  value={scheduleTime2.split(":")[0] || ""}
                  onChange={(e) => {
                    const hour = e.target.value.padStart(2, "0");
                    const minute = scheduleTime2.split(":")[1] || "00";
                    const next = `${hour}:${minute}`;
                    setScheduleTime2(next);
                  }}
                  className="border rounded-lg px-4 py-2"
                  disabled={scheduleLoading2}
                >
                  <option value="">Hour</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option
                      key={i}
                      value={String(i).padStart(2, "0")}
                    >
                      {String(i).padStart(2, "0")}
                    </option>
                  ))}
                </select>

                <select
                  value={scheduleTime2.split(":")[1] || ""}
                  onChange={(e) => {
                    const minute = e.target.value.padStart(2, "0");
                    const hour = scheduleTime2.split(":")[0] || "00";
                    const next = `${hour}:${minute}`;
                    setScheduleTime2(next);
                  }}
                  className="border rounded-lg px-4 py-2"
                  disabled={scheduleLoading2}
                >
                  <option value="">Minute</option>
                  <option value="00">00</option>
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                </select>

                <div className="flex items-center gap-2">
                  {/* SMALL TOGGLE for Schedule B */}
                  <label className="small-toggle">
                    <input
                      type="checkbox"
                      checked={scheduleEnabled2}
                      onChange={handleToggleScheduleB}
                      disabled={scheduleLoading2}
                    />
                    <span
                      className={`small-toggle-pill ${
                        scheduleEnabled2 ? "on" : "off"
                      }`}
                    >
                      <span className="small-toggle-dot" />
                      <span className="small-toggle-label">
                        {scheduleEnabled2 ? "ON" : "OFF"}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {(msgSchedule2 || errorSchedule2) && (
                <div
                  className={`mt-3 px-3 py-2 rounded-lg ${
                    errorSchedule2
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-900"
                  }`}
                >
                  {errorSchedule2 || msgSchedule2}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* two-col end */}
      </div>

      {/* ===== GLOBAL CONFIRM POPUP ===== */}
      {confirmPopup.open && (
        <div className="popup-overlay">
          <div className="popup-box popup-animate">
            <button className="popup-close" onClick={closeConfirmPopup}>
              ✕
            </button>

            <h2 className="popup-title">
              {confirmPopup.title || "Confirm"}
            </h2>
            <p className="popup-message">
              {confirmPopup.message || "Are you sure?"}
            </p>

            {confirmPopup.variant === "info" ? (
              // Info-only popup: single OK button
              <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                <button
                  className="popup-button popup-basic"
                  onClick={closeConfirmPopup}
                >
                  OK
                </button>
              </div>
            ) : (
              // Confirm popup: Cancel + Confirm
              <div className="popup-btn-group1">
                <button
                  className="popup-button popup-basic"
                  onClick={closeConfirmPopup}
                >
                  Cancel
                </button>
                <button
                  className="popup-button popup-error"
                  onClick={handleConfirmPopup}
                >
                  Confirm
                </button>
              </div>
              
            )}
          </div>
        </div>
      )}
    </div>
  );
} 